import React, { useCallback, useEffect, useState } from 'react'
import { useAxios } from '../../core/AxiosContext'
import { useGlobal, useGlobalDispatch } from '../../core/GlobalContext'
import { PROFILE_DUCK, REQUEST_STATUS, TOAST_DUCK, VALIDATION_STATUS } from '../../core/actions'
import {
  fetchFinancierAction,
  getBankNameFromIFSC,
  getVerifiedBankAccountAction,
} from '../../routes/financier/FinancierDuck'
import { validateBankAccountAttributes } from '../../utils/bankAccountValidations'
import Button from '../button'
import ChangeBankAccountAttribute from '../changeBankAccountAttribute'
import Text from '../text'
import * as styles from './ChangeBankAccount.css'
import { TRADECRED_SUPPORT_EMAIL } from '../../core/constants'

export const ChangeBankAccount = ({ handleOnSubmit, handleClose, title }) => {
  const {
    financierState: { financier, updateBank, updateBankMessages },
  } = useGlobal()
  const { financierDispatch, toastDispatch } = useGlobalDispatch()
  const { authAxios } = useAxios()

  const [submitStatus, setSubmitStatus] = useState(REQUEST_STATUS.IDLE)

  useEffect(() => {
    // Set initial state
    financierDispatch({
      type: PROFILE_DUCK.UPDATE_BANK_VALUES,
      payload: {
        updateBank: {
          accountNumber: financier?.financierBank?.accountNumber || '',
          reAccountNumber: financier?.financierBank?.accountNumber || '',
          holderName: financier?.financierBank?.holderName || financier?.financierName || '',
          ifsc: financier?.financierBank?.IFSC || '',
          bankName: financier?.financierBank?.bankName || '',
        },
      },
    })
  }, [])

  // Get BankName only for valid ifsc codes & to stop further retries
  const [prevValidIfsc, setPrevValidIfsc] = useState('')
  useEffect(() => {
    if (prevValidIfsc !== updateBank?.ifsc && updateBankMessages?.ifsc?.status === VALIDATION_STATUS.SUCCESS) {
      setPrevValidIfsc(updateBank?.ifsc)
      getBankNameFromIFSC(updateBank?.ifsc)(authAxios, financierDispatch)
    }
  }, [prevValidIfsc, updateBank?.ifsc, updateBankMessages?.ifsc])

  const successful_transaction = response => {
    console.log('Added bank details successfully', response)
    toastDispatch({
      type: TOAST_DUCK.SEND_TOAST,
      payload: { message: 'Changed bank details successfully', type: REQUEST_STATUS.SUCCESS },
    })
    setSubmitStatus(REQUEST_STATUS.SUCCESS)
    fetchFinancierAction()(authAxios, financierDispatch)
    // This call will be made unwantedly for normal change bank account flow & useful only for deposit bank account flow
    getVerifiedBankAccountAction(financier?.financierPAN)(authAxios, financierDispatch)
  }
  const transaction_failed = error => {
    console.log('Failed to add bank details', error)
    toastDispatch({
      type: TOAST_DUCK.SEND_TOAST,
      payload: {
        message: error || 'Unable to change bank details. Please try after some time.',
        type: REQUEST_STATUS.FAILURE,
      },
    })
    setSubmitStatus(REQUEST_STATUS.FAILURE)
  }

  const handleChangeBankAccount = () => {
    console.log('Change bank acc')
    const data = {
      financier_code: financier?.financierPAN,
      bank_account_code: financier?.financierBank?.id ? financier.financierBank.id : null,
      phone: financier?.authorizedUser?.phone,
      account_number: updateBank.reAccountNumber,
      account_holder_name: updateBank.holderName,
      bank_name: updateBank.bankName,
      ifsc: updateBank.ifsc,
    }

    handleOnSubmit(data, financier?.financierPAN)(authAxios)(successful_transaction, transaction_failed)
    handleClose()
    setSubmitStatus(REQUEST_STATUS.PENDING)
  }

  // Validating every entry field on change
  useEffect(() => {
    Object.entries(updateBank).map(([key, val]) => {
      validateBankAccountAttributes(key, val)({ updateBank, updateBankMessages }, financierDispatch, authAxios)
    })
  }, [updateBank])

  const requiredKeysWithFailureStatus = useCallback(() => {
    const validSubmitTypes = ['accountNumber', 'reAccountNumber', 'holderName', 'ifsc', 'bankName']

    return validSubmitTypes?.filter(key => {
      return updateBankMessages[key]?.status !== 'SUCCESS'
    })
  }, [updateBankMessages])

  return (
    <div>
      <div className={styles.editAttributes}>
        <ChangeBankAccountAttribute attr={{ title: 'Account Number', type: 'accountNumber' }} />
        <ChangeBankAccountAttribute
          attr={{ title: 'Re enter Account Number', placeholder: '* * * * * * * * * *', type: 'reAccountNumber' }}
        />
        <ChangeBankAccountAttribute
          attr={{ title: 'Account Holder Name', placeholder: 'Name', type: 'holderName' }}
          disabled
        />
        <ChangeBankAccountAttribute attr={{ title: 'IFSC Code', placeholder: 'HDFC0123456', type: 'ifsc' }} />
        <ChangeBankAccountAttribute attr={{ title: 'Bank Name', type: 'bankName' }} disabled />
      </div>
      <Text size='0.8' color='var(--grey-text-subtitle-color)'>
        Bank account updation can be done only once in a month. Please reach us out at ${TRADECRED_SUPPORT_EMAIL} for
        further assistance.
      </Text>
      <br />
      <Button
        className={styles.button}
        label={submitStatus === REQUEST_STATUS.PENDING ? 'In Progress ...' : title}
        handleClick={handleChangeBankAccount}
        disabled={requiredKeysWithFailureStatus()?.length !== 0 || submitStatus === REQUEST_STATUS.PENDING}
      />
    </div>
  )
}
